/**
 * BreadcrumbNav
 *
 * Desktop breadcrumb navigation showing the current location path.
 * Allows quick navigation back to previous locations or campus overview.
 */

import React, { Fragment } from 'react';
import { ChevronRight, Home, Map } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { CampusLocation } from '../../../types/spatial';
import { useSpatialNavigation } from '../../../hooks/useSpatialNavigation';
import { getLocationConfig } from '../../../config/locationConfig';
import { useSpatialStore, selectBreadcrumbTrail } from '../../../stores/spatialStore';

export interface BreadcrumbNavProps {
  /** Whether to show in compact mode (tablet) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({
  compact = false,
  className = '',
}) => {
  const { currentLocation, goToCampus, navigateTo, isAtCampus } = useSpatialNavigation();
  const breadcrumbTrail = useSpatialStore(useShallow(selectBreadcrumbTrail));

  // Get unique locations for breadcrumb (avoid duplicates)
  const uniqueTrail = breadcrumbTrail.filter(
    (location, index, arr) => arr.indexOf(location) === index
  );

  // In compact mode, only show current location
  if (compact) {
    const currentConfig = getLocationConfig(currentLocation);
    return (
      <nav
        className={`flex items-center gap-2 ${className}`}
        aria-label="Breadcrumb navigation"
      >
        <button
          onClick={goToCampus}
          className="flex items-center gap-1 px-2 py-1 rounded-lg
                     bg-white/10 hover:bg-white/15
                     text-emerald-400 transition-colors"
          aria-label="Return to campus overview"
        >
          <Map className="w-4 h-4" />
        </button>
        <ChevronRight className="w-3 h-3 text-white/30" />
        <span className="text-sm font-medium text-white">
          {currentConfig.name}
        </span>
      </nav>
    );
  }

  return (
    <nav
      className={`flex items-center gap-2 ${className}`}
      aria-label="Breadcrumb navigation"
    >
      {/* Campus Overview Button */}
      <button
        onClick={goToCampus}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                   transition-colors ${
                     isAtCampus
                       ? 'bg-emerald-500 text-white'
                       : 'bg-white/10 hover:bg-white/15 text-emerald-400'
                   }`}
        aria-label="Campus overview"
        aria-current={isAtCampus ? 'page' : undefined}
      >
        <Home className="w-4 h-4" />
        <span className="text-sm font-medium">Campus</span>
      </button>

      {/* Breadcrumb Trail */}
      {!isAtCampus && uniqueTrail.length > 0 && (
        <>
          {uniqueTrail
            .filter((loc) => loc !== CampusLocation.CAMPUS_OVERVIEW)
            .slice(-3) // Show last 3 locations max
            .map((location) => {
              const config = getLocationConfig(location);
              const isCurrent = location === currentLocation;

              return (
                <Fragment key={location}>
                  <ChevronRight className="w-3 h-3 text-white/30" />
                  <button
                    onClick={() => !isCurrent && navigateTo(location)}
                    disabled={isCurrent}
                    className={`px-2 py-1 rounded text-sm transition-colors ${
                      isCurrent
                        ? 'font-semibold text-white cursor-default'
                        : 'text-white/50 hover:text-white hover:bg-white/10'
                    }`}
                    aria-current={isCurrent ? 'page' : undefined}
                  >
                    {config.name}
                  </button>
                </Fragment>
              );
            })}
        </>
      )}
    </nav>
  );
};

export default BreadcrumbNav;
